import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  accountTermsApi,
  type AccountDto,
  type SendTermsForAcceptanceRequest,
} from "@/lib/clients-accounts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DetailModal } from "@/components/detail-modal";
import { toast } from "@/hooks/use-toast";

// ---------------------------------------------------------------------------
// Request-terms-approval dialog
//
// Wraps POST /api/accounts/{id}/terms/send-for-acceptance, which emails the
// customer an external magic-link to accept T&C. NOTE: the endpoint's
// SendTermsForAcceptanceRequest only accepts `termsId` + `overrideRecipientEmail`
// — no cc/bcc/message fields exist server-side, so this dialog intentionally
// does not offer them (do not add unsupported params).
// ---------------------------------------------------------------------------

export function RequestTermsApprovalDialog({
  open,
  onOpenChange,
  account,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  account: AccountDto;
}) {
  const qc = useQueryClient();
  const [termsId, setTermsId] = useState<string>("");
  const [recipientEmail, setRecipientEmail] = useState(account.emailAddress ?? "");

  const { data: termsList } = useQuery({
    queryKey: ["account-terms-list"],
    queryFn: () => accountTermsApi.list(),
    staleTime: 60_000,
    enabled: open,
  });

  function resetForm() {
    setTermsId("");
    setRecipientEmail(account.emailAddress ?? "");
  }

  const sendMut = useMutation({
    mutationFn: (body: SendTermsForAcceptanceRequest) => accountTermsApi.send(account.accountId, body),
    onSuccess: () => {
      toast.success("Terms approval request sent");
      void qc.invalidateQueries({ queryKey: ["account", account.accountId] });
      onOpenChange(false);
      resetForm();
    },
    onError: () => toast.error("Failed to send terms approval request"),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!termsId || !recipientEmail) return;
    sendMut.mutate({
      termsId: Number(termsId),
      overrideRecipientEmail: recipientEmail || null,
    });
  }

  return (
    <DetailModal open={open} onOpenChange={onOpenChange} title="Request terms approval">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="rta-terms">Terms version *</Label>
          <Select value={termsId} onValueChange={setTermsId}>
            <SelectTrigger id="rta-terms">
              <SelectValue placeholder="Select terms version" />
            </SelectTrigger>
            <SelectContent>
              {(termsList?.items ?? []).map((t) => (
                <SelectItem key={t.termsId} value={String(t.termsId)}>
                  v{t.versionNumber} — {t.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="rta-recipient">Recipient email *</Label>
          <Input
            id="rta-recipient"
            type="email"
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
            required
          />
        </div>
        <Button type="submit" disabled={sendMut.isPending || !termsId || !recipientEmail}>
          {sendMut.isPending ? "Sending…" : "Send"}
        </Button>
      </form>
    </DetailModal>
  );
}
